// School-day streak. Counts school days only: the weekdays from the Class Bell settings (Monday to
// Friday unless the student changed them), minus days the student marks off, minus the summer
// break. Weekends, days off and summer never break it.
//
// A school day counts after real work: one finished focus session, or 5 flashcards reviewed.
// A missed school day uses a freeze if there is one (earned: one per 5 counted days, at most 2,
// never bought), otherwise the streak quietly starts again. Nothing here talks to the server.
//
// Plain functions over the study ledger (shared/activity.js) and a small state object, so the
// start page, the Island and the tests share them. The state keeps a checkpoint (`at`) and the
// last WINDOW days are worked out again from the ledger on every read, so marking a recent day
// off fixes the streak after the fact.
// Same day keys as shared/activity.js (local dates, stepped at noon so DST never skips a day),
// copied so the lazy chunks that use this don't pull in the ledger module a second time.
const p2 = (n) => (n < 10 ? "0" : "") + n;
export const dateKey = (d = new Date()) => d.getFullYear() + "-" + p2(d.getMonth() + 1) + "-" + p2(d.getDate());
export const fromKey = (key) => { const [y, m, d] = String(key).split("-").map(Number); return new Date(y, m - 1, d, 12); };
export const dayDiff = (a, b) => Math.round((fromKey(b) - fromKey(a)) / 864e5);

export const STREAK_KEY = "revault:streak";
// Not under "revault:", so it never goes into a backup. A state whose token doesn't match this
// browser's came back from a backup or another device.
export const DEVICE_KEY = "rv-streak-device";
export const FREEZE_EVERY = 5;
export const FREEZE_MAX = 2;
export const WINDOW = 14;
export const CARDS_TO_COUNT = 5;
const MAX_STEPS = 400;
const OFF_MAX = 60;
const KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const MD_RE = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
// Wide on purpose: most US schools are out by late May or early June and back by late August.
// A day inside the break only pauses the streak, so too wide costs nothing.
export const SUMMER_DEFAULT = { from: "05-25", to: "08-31" };

const addDays = (key, n) => { const d = fromKey(key); return dateKey(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, 12)); };
export { addDays };

// The school calendar from settings: { days: [1..5], summer: { from, to } | null, off: Set }.
export function calendar(get, off = []) {
	const days = Array.isArray(get("bellDays")) ? get("bellDays").filter((d) => Number.isInteger(d) && d >= 0 && d <= 6) : [1, 2, 3, 4, 5];
	const on = get("streakSummer") !== false;
	const from = MD_RE.test(get("streakSummerFrom")) ? get("streakSummerFrom") : SUMMER_DEFAULT.from;
	const to = MD_RE.test(get("streakSummerTo")) ? get("streakSummerTo") : SUMMER_DEFAULT.to;
	return { days: days.length ? days : [1, 2, 3, 4, 5], summer: on ? { from, to } : null, off: new Set(off) };
}

// "MM-DD" inside the break. A break that runs over New Year (from > to) wraps.
export function inSummer(key, summer) {
	if (!summer) return false;
	const md = key.slice(5);
	return summer.from <= summer.to ? md >= summer.from && md <= summer.to : md >= summer.from || md <= summer.to;
}
export function isSchoolDay(key, cal) {
	return cal.days.includes(fromKey(key).getDay()) && !cal.off.has(key) && !inSummer(key, cal.summer);
}
// Real work that day: a finished focus session, or 5 cards reviewed (or marked known).
export function dayCounts(rec) {
	return !!rec && ((rec.fo | 0) >= 1 || (rec.fr | 0) >= CARDS_TO_COUNT || (rec.fc | 0) >= CARDS_TO_COUNT);
}

export function fresh(today, dev = "") {
	return { v: 1, at: addDays(today, -(WINDOW + 1)), cur: 0, best: 0, frz: 0, prog: 0, last: "", shown: 0, sf: 0, sp: 0, off: [], dev, wb: 0, wbAt: "" };
}

const num = (v, max = 1e6) => (Number.isInteger(v) && v >= 0 ? Math.min(v, max) : 0);
// Reads tolerate anything; a broken state becomes a fresh one.
export function clean(raw, today) {
	if (!raw || typeof raw !== "object" || !KEY_RE.test(raw.at) || raw.at >= today) return null;
	return {
		v: 1, at: raw.at, cur: num(raw.cur), best: Math.max(num(raw.best), num(raw.cur)), frz: num(raw.frz, FREEZE_MAX), prog: num(raw.prog, FREEZE_EVERY - 1),
		last: KEY_RE.test(raw.last) ? raw.last : "", shown: num(raw.shown), sf: num(raw.sf, FREEZE_MAX), sp: num(raw.sp, FREEZE_EVERY - 1),
		off: Array.isArray(raw.off) ? raw.off.filter((k) => KEY_RE.test(k)).sort().slice(-OFF_MAX) : [],
		dev: typeof raw.dev === "string" ? raw.dev.slice(0, 24) : "", wb: raw.wb === 1 ? 1 : 0, wbAt: KEY_RE.test(raw.wbAt) ? raw.wbAt : "",
	};
}

// One calendar day. Returns what happened: "count", "freeze", "reset", "wait" (today, not done
// yet) or "rest" (not a school day).
function step(s, key, rec, cal, today, forgive) {
	if (!isSchoolDay(key, cal)) return "rest";
	if (dayCounts(rec)) {
		s.cur++;
		s.best = Math.max(s.best, s.cur);
		s.last = key;
		if (++s.prog >= FREEZE_EVERY) { s.prog = 0; s.frz = Math.min(FREEZE_MAX, s.frz + 1); }
		return "count";
	}
	if (key === today) return "wait";
	if (forgive) return "rest";
	if (s.frz > 0) { s.frz--; return "freeze"; }
	s.cur = 0;
	return "reset";
}

// Fold the days after s.at through `through` (inclusive). Returns { s, marks: { key: result } }.
function fold(state, ledger, cal, through, today, forgive = false) {
	const s = { ...state };
	const marks = {};
	let key = s.at;
	let n = dayDiff(key, through);
	if (n > MAX_STEPS) { key = addDays(through, -MAX_STEPS); s.cur = 0; n = MAX_STEPS; } // gone a long time
	for (let i = 0; i < n; i++) {
		key = addDays(key, 1);
		marks[key] = step(s, key, ledger[key], cal, today, forgive);
	}
	s.at = through;
	return { s, marks };
}

// What to show now. Never changes the stored state.
export function view(state, ledger, cal, now = new Date()) {
	const today = dateKey(now);
	const { s, marks } = fold(state, ledger, cal, today, today);
	const todaySchool = isSchoolDay(today, cal);
	return {
		cur: s.cur, best: s.best, frz: s.frz, prog: s.prog,
		today: marks[today] || "rest", todaySchool, summer: inSummer(today, cal.summer),
		marks, wb: state.wb === 1,
	};
}

// Move the checkpoint up to WINDOW days before today, so later reads stay short and the ledger
// (kept 70 days) always still has the days being worked out again.
export function settle(state, ledger, cal, now = new Date()) {
	const today = dateKey(now);
	const cut = addDays(today, -(WINDOW + 1));
	if (state.at >= cut) return state;
	return fold(state, ledger, cal, cut, today).s;
}

// The ledger lost the last day this state counted (cleared site data, an old backup): the
// streak would look broken for no reason.
export function ledgerLost(state, ledger, now = new Date()) {
	if (!state.last || !(state.cur > 0 || state.shown > 0)) return false;
	const gap = dayDiff(state.last, dateKey(now));
	return gap >= 1 && gap <= 60 && !dayCounts(ledger[state.last]);
}

// "Welcome back": the streak survives as it was last shown, with the missing days forgiven, plus
// one free freeze (at most once in 30 days, so two devices swapping backups can't mint freezes).
export function welcomeBack(state, ledger, cal, now = new Date(), dev = state.dev) {
	const today = dateKey(now);
	const yest = addDays(today, -1);
	const s = state.at < yest ? fold(state, ledger, cal, yest, today, true).s : { ...state };
	if (state.shown >= s.cur) { s.cur = state.shown; s.frz = Math.max(s.frz, state.sf); s.prog = state.sp; }
	s.best = Math.max(s.best, s.cur);
	s.dev = dev;
	s.last = "";
	if (!s.wbAt || dayDiff(s.wbAt, today) >= 30) { s.frz = Math.min(FREEZE_MAX, s.frz + 1); s.wbAt = today; s.wb = 1; }
	return s;
}

// Load, repair and settle in one go. dev: this browser's token (DEVICE_KEY).
// Returns { state, view, changed }.
export function load(raw, ledger, cal, now = new Date(), dev = "") {
	const today = dateKey(now);
	let s = clean(raw, today);
	let changed = false;
	if (!s) { s = fresh(today, dev); changed = true; }
	else {
		const moved = !!(s.dev && dev && s.dev !== dev); // came back from a backup or another device
		// Moving between two browsers (cloud sync) is forgiven once in 30 days. After that the
		// ledger that synced along with it decides, like on any other day.
		const again = moved && s.wbAt && dayDiff(s.wbAt, today) < 30;
		if (!again && (moved || ledgerLost(s, ledger, now)) && (s.cur > 0 || s.shown > 0)) { s = welcomeBack(s, ledger, cal, now, dev); changed = true; }
		else if (dev && s.dev !== dev) { s.dev = dev; changed = true; }
	}
	const t = settle(s, ledger, cal, now);
	if (t !== s) { s = t; changed = true; }
	const v = view(s, ledger, cal, now);
	// Remember the newest counted day and the streak through yesterday, for spotting a lost ledger
	// and for a welcome back.
	let last = s.last;
	for (const k in v.marks) if (v.marks[k] === "count" && k < today && k > last) last = k;
	const yest = addDays(today, -1);
	const y = s.at < yest ? fold(s, ledger, cal, yest, today).s : s;
	const best = Math.max(s.best, v.best); // Quests reads this for its milestones
	if (last !== s.last || y.cur !== s.shown || y.frz !== s.sf || y.prog !== s.sp || best !== s.best) { s = { ...s, last, shown: y.cur, sf: y.frz, sp: y.prog, best }; changed = true; }
	return { state: s, view: v, changed };
}

// Mark a day off (or back on): today, the last WINDOW days (older ones are settled) or up to a
// month ahead.
const AHEAD = 31;
export function canMarkOff(key, now = new Date()) {
	const d = dayDiff(key, dateKey(now));
	return d > -AHEAD && d < WINDOW;
}
export function toggleOff(state, key, now = new Date()) {
	if (!KEY_RE.test(key) || !canMarkOff(key, now)) return state;
	const off = state.off.includes(key) ? state.off.filter((k) => k !== key) : [...state.off, key].sort().slice(-OFF_MAX);
	return { ...state, off };
}

// The school days the days-off picker offers: the last WINDOW days that can still change, then the
// rest of this week (to Sunday). Weekend days and the bell's other days are left out. Days from
// before this week carry their date, so last Friday's snow day reads "Fri 16".
const SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export function offChoices(days, now = new Date()) {
	const today = dateKey(now);
	const dow = fromKey(today).getDay();
	const monday = addDays(today, -((dow + 6) % 7));
	const out = [];
	for (let key = addDays(today, -(WINDOW - 1)); key <= addDays(monday, 6); key = addDays(key, 1)) {
		const d = fromKey(key);
		if (!days.includes(d.getDay()) || !canMarkOff(key, now)) continue;
		out.push({ key, label: SHORT[d.getDay()] + (key < monday ? " " + d.getDate() : "") + (key === today ? " (today)" : ""), past: key < monday });
	}
	return out;
}

// Words. Calm on purpose: no "lost", no "don't break it", nothing at night.
export function streakText(v) {
	if (v.summer) return v.cur ? "Summer break. Your streak of " + v.cur + " waits for school." : "Summer break. The streak starts again with school.";
	if (!v.cur) return v.todaySchool ? "Start a school-day streak: one focus session or 5 cards." : "School-day streak starts on your next school day.";
	const days = v.cur + (v.cur === 1 ? " school day" : " school days");
	if (v.today === "count") return days + ". Today counts.";
	if (!v.todaySchool) return days + ". No school today.";
	return days + ". One focus session or 5 cards adds today.";
}
export function freezeText(v) {
	if (v.summer) return "";
	if (v.frz) return (v.frz === 1 ? "1 freeze ready" : v.frz + " freezes ready") + ". It fills in for a busy school day on its own.";
	const left = FREEZE_EVERY - v.prog;
	return "A freeze after " + left + " more counted " + (left === 1 ? "day" : "days") + ".";
}
// Quiet hours: nothing about the streak pops up between 9 pm and 7 am.
export function quietHour(now = new Date()) { const h = now.getHours(); return h >= 21 || h < 7; }
